import React, { useEffect, useState } from "react";
import { Lock, LogOut, RefreshCw, DollarSign, Users, Package, Mail, X, Send, TrendingUp, ShoppingCart, Eye, MousePointer } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";

const STORAGE_KEY = "contentsnap_admin_pw";

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="snap-card">
    <div className="flex items-center gap-3">
      <span className="w-10 h-10 rounded-md bg-[#3B9EFF]/10 border border-[#3B9EFF]/30 flex items-center justify-center text-[#3B9EFF]">
        <Icon size={18} />
      </span>
      <span className="font-accent text-xs text-[#A0AABF]">{label}</span>
    </div>
    <p className="mt-3 font-display text-4xl font-black text-white">{value}</p>
    {sub && <p className="mt-1 text-xs text-[#A0AABF]">{sub}</p>}
  </div>
);

const FunnelRow = ({ label, value, pct, color = "#3B9EFF" }) => (
  <div className="py-3 border-b border-[#3B9EFF]/10 last:border-0">
    <div className="flex items-center justify-between mb-2">
      <span className="text-white font-display font-semibold">{label}</span>
      <span className="font-display font-bold text-2xl text-white">{value}</span>
    </div>
    <div className="h-2 bg-white/[0.04] rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
    </div>
    <p className="text-xs text-[#A0AABF] mt-1">{pct}% of top of funnel</p>
  </div>
);

const Tab = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2 rounded-md text-sm font-display font-semibold transition-colors ${
      active ? "bg-[#3B9EFF] text-white" : "text-[#A0AABF] hover:text-white"
    }`}
  >
    {children}
  </button>
);

export default function AdminPage() {
  const [password, setPassword] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("analytics");
  const [orders, setOrders] = useState({ orders: [], stats: {} });
  const [subs, setSubs] = useState({ subscriptions: [], stats: {} });
  const [intakes, setIntakes] = useState({ intakes: [], count: 0 });
  const [leads, setLeads] = useState({ leads: [], count: 0 });
  const [analytics, setAnalytics] = useState({ last_7_days: {}, last_30_days: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [recoveryRunning, setRecoveryRunning] = useState(false);

  const headers = () => ({ "X-Admin-Password": password });

  const tryLogin = async (e) => {
    e?.preventDefault?.();
    setError("");
    try {
      await api.post("/admin/login", { password });
      localStorage.setItem(STORAGE_KEY, password);
      setAuthed(true);
      loadAll();
    } catch (err) {
      setError("Wrong password");
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, o, s, i, l] = await Promise.all([
        api.get("/admin/analytics", { headers: headers() }),
        api.get("/admin/orders", { headers: headers() }),
        api.get("/admin/subscriptions", { headers: headers() }),
        api.get("/admin/intake-submissions", { headers: headers() }),
        api.get("/admin/leads", { headers: headers() }),
      ]);
      setAnalytics(a.data);
      setOrders(o.data);
      setSubs(s.data);
      setIntakes(i.data);
      setLeads(l.data);
      setAuthed(true);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthed(false);
        localStorage.removeItem(STORAGE_KEY);
      } else {
        setError("Could not load. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelSub = async (subId) => {
    if (!window.confirm("Cancel this subscription? Customer will be emailed.")) return;
    try {
      await api.post(`/admin/subscriptions/${subId}/cancel`, {}, { headers: headers() });
      loadAll();
    } catch {
      alert("Could not cancel.");
    }
  };

  const runRecovery = async () => {
    if (!window.confirm("Send recovery emails to all abandoned checkouts > 30 min old that haven't been emailed yet?")) return;
    setRecoveryRunning(true);
    try {
      const r = await api.post("/admin/run-abandoned-cart-recovery", {}, { headers: headers() });
      alert(`Sent ${r.data.emails_sent} recovery email${r.data.emails_sent === 1 ? "" : "s"}.`);
      loadAll();
    } catch {
      alert("Recovery run failed.");
    } finally {
      setRecoveryRunning(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setPassword("");
    setAuthed(false);
  };

  useEffect(() => {
    if (password) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authed) {
    return (
      <div className="bg-grain min-h-screen" data-testid="admin-login-page">
        <Header />
        <main className="py-24">
          <div className="max-w-md mx-auto px-6">
            <div className="snap-card">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-md bg-[#3B9EFF]/10 border border-[#3B9EFF]/30 flex items-center justify-center text-[#3B9EFF]">
                  <Lock size={18} />
                </span>
                <h1 className="font-display text-2xl font-bold text-white">Admin Access</h1>
              </div>
              <form onSubmit={tryLogin} className="mt-6 space-y-4">
                <input
                  type="password"
                  placeholder="Admin password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="snap-input"
                  data-testid="admin-password-input"
                  autoFocus
                />
                {error && <p className="text-sm text-red-400" data-testid="admin-error">{error}</p>}
                <button type="submit" className="btn-primary w-full justify-center" data-testid="admin-login-btn">
                  Sign in
                </button>
              </form>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const a7 = analytics.last_7_days || {};
  const a30 = analytics.last_30_days || {};
  // Funnel percentages relative to offer_views (top)
  const top = Math.max(a7.offer_views || 1, 1);
  const pct = (v) => Math.round(((v || 0) / top) * 100);

  return (
    <div className="bg-grain min-h-screen" data-testid="admin-page">
      <Header />
      <main className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
            <div>
              <span className="pill">Admin Console</span>
              <h1 className="mt-3 font-display text-4xl md:text-5xl font-black text-white tracking-tighter">
                ContentSnap <span className="text-[#3B9EFF]">control</span>
              </h1>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={runRecovery} disabled={recoveryRunning} className="btn-secondary text-sm" data-testid="admin-run-recovery">
                <Send size={14} /> {recoveryRunning ? "Sending..." : "Send Recovery Emails"}
              </button>
              <button onClick={loadAll} className="btn-secondary text-sm" data-testid="admin-refresh">
                <RefreshCw size={14} /> Refresh
              </button>
              <button onClick={logout} className="btn-secondary text-sm" data-testid="admin-logout">
                <LogOut size={14} /> Logout
              </button>
            </div>
          </div>

          {/* Top KPIs */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={DollarSign} label="REVENUE (30D)" value={`$${(a30.revenue_usd || 0).toFixed(2)}`} sub={`$${(a7.revenue_usd || 0).toFixed(2)} last 7d`} />
            <StatCard icon={Package} label="PAID ORDERS (30D)" value={a30.orders_paid || 0} sub={`${a7.orders_paid || 0} last 7d`} />
            <StatCard icon={Users} label="ACTIVE SUBSCRIBERS" value={subs.stats?.active || 0} sub={`$${(subs.stats?.monthly_recurring_value || 0).toFixed(2)} MRR`} />
            <StatCard icon={ShoppingCart} label="ABANDONED (30D)" value={a30.abandoned_checkouts || 0} sub={`${a30.checkout_completion_rate || 0}% completion`} />
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-[#3B9EFF]/15 mb-6 flex-wrap">
            <Tab active={tab === "analytics"} onClick={() => setTab("analytics")} testId="tab-analytics">Analytics</Tab>
            <Tab active={tab === "orders"} onClick={() => setTab("orders")} testId="tab-orders">Orders</Tab>
            <Tab active={tab === "leads"} onClick={() => setTab("leads")} testId="tab-leads">Leads</Tab>
            <Tab active={tab === "subs"} onClick={() => setTab("subs")} testId="tab-subs">Subscriptions</Tab>
            <Tab active={tab === "intakes"} onClick={() => setTab("intakes")} testId="tab-intakes">Intake Forms</Tab>
          </div>

          {loading && <p className="text-[#A0AABF]">Loading...</p>}

          {tab === "analytics" && (
            <div className="grid lg:grid-cols-2 gap-6" data-testid="analytics-panel">
              <div className="snap-card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-2xl font-bold text-white">Conversion Funnel</h2>
                  <span className="font-accent text-xs text-[#3B9EFF]">LAST 7 DAYS</span>
                </div>
                <FunnelRow label="Offer Page Views" value={a7.offer_views || 0} pct={100} />
                <FunnelRow label="Lead Forms Captured" value={a7.leads_captured || 0} pct={pct(a7.leads_captured)} />
                <FunnelRow label="Reached Checkout (Pay clicked)" value={a7.checkouts_initiated || 0} pct={pct(a7.checkouts_initiated)} />
                <FunnelRow label="Completed Orders" value={a7.orders_paid || 0} pct={pct(a7.orders_paid)} color="#22c55e" />
              </div>

              <div className="snap-card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-2xl font-bold text-white">Key Rates</h2>
                  <span className="font-accent text-xs text-[#3B9EFF]">LAST 7 DAYS</span>
                </div>
                <div className="space-y-3">
                  <RateRow label="Offer view → Lead" value={`${a7.offer_view_to_lead_rate || 0}%`} hint={a7.offer_view_to_lead_rate >= 10 ? "✅ healthy" : "⚠ try shorter form"} />
                  <RateRow label="Lead → Checkout" value={`${a7.lead_to_checkout_rate || 0}%`} hint={a7.lead_to_checkout_rate >= 30 ? "✅ healthy" : "⚠ improve offer clarity"} />
                  <RateRow label="Checkout → Paid" value={`${a7.checkout_completion_rate || 0}%`} hint={a7.checkout_completion_rate >= 50 ? "✅ healthy" : "⚠ run recovery emails"} />
                </div>
                <div className="mt-6 pt-5 border-t border-[#3B9EFF]/10">
                  <p className="font-accent text-xs text-[#A0AABF] mb-1">LINK CLICKS FROM ADS</p>
                  <p className="text-sm text-[#A0AABF]">
                    Track in <a href="https://www.facebook.com/adsmanager" target="_blank" rel="noopener noreferrer" className="text-[#3B9EFF] hover:underline">Meta Ads Manager</a> — those numbers live with Facebook, not in our database.
                  </p>
                </div>
              </div>

              <div className="snap-card lg:col-span-2">
                <h2 className="font-display text-2xl font-bold text-white mb-4">Last 30 Days vs Last 7 Days</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <MiniStat label="Offer Views" v7={a7.offer_views || 0} v30={a30.offer_views || 0} />
                  <MiniStat label="Leads" v7={a7.leads_captured || 0} v30={a30.leads_captured || 0} />
                  <MiniStat label="Checkouts" v7={a7.checkouts_initiated || 0} v30={a30.checkouts_initiated || 0} />
                  <MiniStat label="Paid Orders" v7={a7.orders_paid || 0} v30={a30.orders_paid || 0} />
                </div>
              </div>
            </div>
          )}

          {tab === "orders" && (
            <div className="snap-card overflow-x-auto p-0" data-testid="orders-table">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.02] text-[#A0AABF] font-accent text-xs">
                  <tr>
                    <th className="text-left p-4">DATE</th>
                    <th className="text-left p-4">PACKAGE</th>
                    <th className="text-left p-4">CUSTOMER</th>
                    <th className="text-right p-4">AMOUNT</th>
                    <th className="text-left p-4">STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.orders.length === 0 && (
                    <tr><td colSpan={5} className="p-8 text-center text-[#A0AABF]">No orders yet.</td></tr>
                  )}
                  {orders.orders.map((o) => (
                    <tr key={o.id} className="border-t border-[#3B9EFF]/10">
                      <td className="p-4 text-[#A0AABF]">{new Date(o.created_at).toLocaleString()}</td>
                      <td className="p-4 text-white">{o.package_name}</td>
                      <td className="p-4 text-[#A0AABF]">{o.customer_email || "—"}</td>
                      <td className="p-4 text-right font-display font-bold text-white">${(o.amount || 0).toFixed(2)}</td>
                      <td className="p-4">
                        <span className={`pill text-[10px] ${o.payment_status === "paid" ? "" : "border-[#A0AABF]/30 text-[#A0AABF]"}`}>
                          {o.payment_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "leads" && (
            <div className="snap-card overflow-x-auto p-0" data-testid="leads-table">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.02] text-[#A0AABF] font-accent text-xs">
                  <tr>
                    <th className="text-left p-4">DATE</th>
                    <th className="text-left p-4">BUSINESS</th>
                    <th className="text-left p-4">EMAIL</th>
                    <th className="text-left p-4">INDUSTRY</th>
                    <th className="text-left p-4">STYLE</th>
                    <th className="text-left p-4">STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.leads.length === 0 && (
                    <tr><td colSpan={6} className="p-8 text-center text-[#A0AABF]">No leads captured yet. They'll appear here when visitors fill the offer-page form.</td></tr>
                  )}
                  {leads.leads.map((l) => (
                    <tr key={l.id} className="border-t border-[#3B9EFF]/10">
                      <td className="p-4 text-[#A0AABF]">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="p-4 text-white">{l.business_name}</td>
                      <td className="p-4 text-[#A0AABF]"><a href={`mailto:${l.email}`} className="hover:text-[#3B9EFF]">{l.email}</a></td>
                      <td className="p-4 text-[#A0AABF]">{l.industry}</td>
                      <td className="p-4 text-[#A0AABF]">{l.content_style}</td>
                      <td className="p-4">
                        <span className={`pill text-[10px] ${l.converted ? "border-green-500/60 text-green-400 bg-green-500/10" : "border-[#A0AABF]/30 text-[#A0AABF]"}`}>
                          {l.converted ? "PAID ✓" : "LEAD"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "subs" && (
            <div className="snap-card overflow-x-auto p-0" data-testid="subs-table">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.02] text-[#A0AABF] font-accent text-xs">
                  <tr>
                    <th className="text-left p-4">STARTED</th>
                    <th className="text-left p-4">CUSTOMER</th>
                    <th className="text-right p-4">AMOUNT/MO</th>
                    <th className="text-left p-4">NEXT RENEWAL</th>
                    <th className="text-left p-4">STATUS</th>
                    <th className="text-right p-4">ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.subscriptions.length === 0 && (
                    <tr><td colSpan={6} className="p-8 text-center text-[#A0AABF]">No subscriptions yet.</td></tr>
                  )}
                  {subs.subscriptions.map((s) => (
                    <tr key={s.id} className="border-t border-[#3B9EFF]/10">
                      <td className="p-4 text-[#A0AABF]">{new Date(s.started_at).toLocaleDateString()}</td>
                      <td className="p-4 text-white">{s.customer_email || "—"}</td>
                      <td className="p-4 text-right font-display font-bold text-white">${(s.amount || 0).toFixed(2)}</td>
                      <td className="p-4 text-[#A0AABF]">{s.next_renewal ? new Date(s.next_renewal).toLocaleDateString() : "—"}</td>
                      <td className="p-4">
                        <span className={`pill text-[10px] ${s.status === "active" ? "" : "border-[#A0AABF]/30 text-[#A0AABF]"}`}>{s.status}</span>
                      </td>
                      <td className="p-4 text-right">
                        {s.status === "active" ? (
                          <button onClick={() => cancelSub(s.id)} className="inline-flex items-center gap-1 text-red-400 hover:text-red-300 text-xs font-semibold" data-testid={`cancel-sub-${s.id}`}>
                            <X size={12} /> Cancel
                          </button>
                        ) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "intakes" && (
            <div className="space-y-4" data-testid="intakes-list">
              {intakes.intakes.length === 0 && (
                <div className="snap-card text-center text-[#A0AABF]">No intake forms submitted yet.</div>
              )}
              {intakes.intakes.map((i) => (
                <div key={i.id} className="snap-card">
                  <div className="flex justify-between items-start gap-4 flex-wrap">
                    <div>
                      <h3 className="font-display text-xl font-bold text-white">{i.business_name}</h3>
                      <p className="text-[#A0AABF] text-sm">{i.industry} · {i.customer_email}</p>
                    </div>
                    <span className="font-accent text-xs text-[#A0AABF]">{new Date(i.created_at).toLocaleString()}</span>
                  </div>
                  <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
                    <Field label="Audience" value={i.target_audience} />
                    <Field label="Tone" value={i.tone_of_voice} />
                    <Field label="Style" value={i.content_style} />
                    <Field label="Brand colors" value={i.brand_colors} />
                    <Field label="Products/Services" value={i.products_services} />
                    <Field label="Reference" value={i.social_or_website} />
                  </div>
                  {i.additional_notes && (
                    <div className="mt-3 text-sm">
                      <p className="font-accent text-xs text-[#A0AABF]">NOTES</p>
                      <p className="text-white mt-1">{i.additional_notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <p className="font-accent text-[10px] text-[#A0AABF]">{label}</p>
    <p className="text-white mt-1">{value || "—"}</p>
  </div>
);

const RateRow = ({ label, value, hint }) => (
  <div className="flex items-center justify-between py-2 border-b border-[#3B9EFF]/10 last:border-0">
    <div>
      <p className="text-white font-display font-semibold">{label}</p>
      <p className="text-xs text-[#A0AABF]">{hint}</p>
    </div>
    <p className="font-display text-2xl font-bold text-[#3B9EFF]">{value}</p>
  </div>
);

const MiniStat = ({ label, v7, v30 }) => (
  <div>
    <p className="font-accent text-[10px] text-[#A0AABF]">{label}</p>
    <p className="font-display text-3xl font-bold text-white mt-1">{v30}</p>
    <p className="text-xs text-[#A0AABF]">7d: {v7}</p>
  </div>
);
